import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Radio } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];
const LS_KEY = "sp_sales_lead";
const REC_KEY = "sp_rec";
const HIDDEN_PREFIXES = ["/admin", "/dev-hub", "/sales", "/portal", "/auth", "/banned", "/join-creators", "/creator", "/sign"];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

function loadLead() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch { return null; }
}

// Capture a sales rep's personal ?rec= link once so leads attribute to them
function captureRec() {
  const fromUrl = new URLSearchParams(window.location.search).get("rec");
  if (fromUrl) localStorage.setItem(REC_KEY, fromUrl);
  return localStorage.getItem(REC_KEY) || "";
}

export default function ProspectChat() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState(loadLead);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [starting, setStarting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  const poll = useCallback(async () => {
    if (!lead) return;
    try {
      const r = await api.get(`/sales/chat/${lead.channel_id}`, { params: { guest: lead.guest_key } });
      setMessages((prev) => {
        if (!open && r.data.messages.length > prev.length && prev.length > 0) setUnread((u) => u + r.data.messages.length - prev.length);
        return r.data.messages;
      });
    } catch { /* conversation may not be ready yet */ }
  }, [lead, open]);

  useEffect(() => {
    if (!lead) return undefined;
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [poll, lead]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);
  useEffect(() => { if (open) setUnread(0); }, [open]);
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener("sp:open-chat", openChat);
    return () => window.removeEventListener("sp:open-chat", openChat);
  }, []);

  if (hidden) return null;

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/sales/lead", { ...form, rec: captureRec() });
      const rec = { lead_id: r.data.lead_id, channel_id: r.data.channel_id, guest_key: r.data.guest_key, name: form.name };
      localStorage.setItem(LS_KEY, JSON.stringify(rec));
      setLead(rec);
      toast.success("Uplink established — a specialist will reply shortly");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post(`/sales/chat/${lead.channel_id}`, { text: draft.trim(), guest_name: lead.name || "" }, { params: { guest: lead.guest_key } });
      setDraft("");
      poll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating action button */}
      <button
        data-testid="prospect-chat-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-20 right-6 md:bottom-6 z-[70] w-14 h-14 bg-nc-blue hover:bg-nc-blue/85 text-white flex items-center justify-center shadow-[0_0_30px_rgba(46,107,255,0.45)] transition-colors duration-300"
        aria-label="Open project chat"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
        {!open && unread > 0 && (
          <span data-testid="prospect-chat-unread" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-nc-orange text-white font-mono text-[10px] flex items-center justify-center">{unread}</span>
        )}
        {!open && !lead && <span className="absolute -top-1 -right-1 w-3 h-3 bg-nc-orange animate-ping" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="prospect-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed bottom-36 right-6 md:bottom-24 z-[70] w-[92vw] max-w-[380px] border border-white/15 bg-[#0a0a0a] shadow-2xl flex flex-col max-h-[70vh]"
          >
            <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between shrink-0">
              <div>
                <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-1.5"><Radio className="w-3 h-3" /> Specpixel // Project Uplink</p>
                <p className="font-mono text-[9px] text-zinc-600 mt-0.5">Direct line to the studio — we reply fast</p>
              </div>
              <button data-testid="prospect-chat-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
            </div>

            {!lead ? (
              <div className="p-4 flex flex-col gap-3 overflow-y-auto">
                <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
                  Tell us what you're building. This opens a tracked channel with our team — scoping, quoting and kickoff all happen right here.
                </p>
                <input data-testid="prospect-name-input" className={inputCls} placeholder="Your name / studio *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <input data-testid="prospect-email-input" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                <textarea data-testid="prospect-message-input" rows={4} className={`${inputCls} resize-none`} placeholder="What do you need built? (engine, scope, timeline…) *" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} />
                <button
                  data-testid="prospect-start-button"
                  disabled={starting || form.name.trim().length < 2 || form.email.trim().length < 5 || form.message.trim().length < 5}
                  onClick={start}
                  className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-3 transition-colors"
                >
                  {starting ? "Establishing…" : "Open Channel"}
                </button>
              </div>
            ) : (
              <>
                <div data-testid="prospect-chat-messages" className="flex-1 overflow-y-auto p-4 flex flex-col gap-2.5 min-h-[240px]">
                  {messages.map((m) => {
                    const mine = m.sender_kind === "guest" || m.sender_kind === "client";
                    return (
                      <div key={m.id} className={`max-w-[85%] px-3 py-2 border ${mine ? "self-end bg-nc-blue/10 border-nc-blue/30" : "self-start bg-white/5 border-white/10"}`}>
                        <p className={`font-mono text-[8px] tracking-[0.2em] uppercase mb-1 ${mine ? "text-nc-blue" : "text-nc-orange"}`}>{m.sender_name}</p>
                        <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.text}</p>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <div className="border-t border-white/10 p-3 flex gap-2 shrink-0">
                  <input
                    data-testid="prospect-chat-input"
                    className={inputCls}
                    placeholder="Type a message…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  />
                  <button data-testid="prospect-chat-send" disabled={sending || !draft.trim()} onClick={send} className="shrink-0 w-11 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white flex items-center justify-center transition-colors">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
